/**
 * Utilitaires pour l'authentification et la gestion JWT
 */

import { isDebugEnabled } from './fetchInterceptor';

/**
 * Décode le payload JWT avec support UTF-8 (pour les noms avec accents)
 * @param {string} base64 - Partie payload du JWT en base64
 * @returns {object} Payload décodé
 */
const decodeBase64UTF8 = (base64) => {
  const binaryString = atob(base64);
  const bytes = Uint8Array.from(binaryString, c => c.charCodeAt(0));
  return JSON.parse(new TextDecoder().decode(bytes));
};

/**
 * Extraire l'ID utilisateur depuis un token JWT
 * @param {string} token - Token JWT à décoder
 * @returns {string|null} ID utilisateur ou null si erreur
 */
export const getUserIdFromToken = (token) => {
  if (!token) return null;
  
  try {
    // Vérifier que le token a 3 parties (header.payload.signature)
    const parts = token.split('.');
    if (parts.length !== 3) {
      console.error('Format JWT invalide: doit avoir 3 parties');
      return null;
    }
    
    // Décoder le payload (partie centrale) avec support UTF-8
    const payload = decodeBase64UTF8(parts[1]);
    if (isDebugEnabled()) {
      console.log('🔍 [DEBUG JWT] Payload décodé:', payload);
    }
    
    // Essayer différents champs possibles pour l'ID utilisateur
    return payload.userId || payload.id || payload.sub || payload.user_id || null;
    
  } catch (error) {
    console.error('Erreur décodage JWT pour userId:', error);
    return null;
  }
};

/**
 * Vérifier si un token JWT est valide structurellement
 * @param {string} token - Token JWT à vérifier
 * @returns {boolean} True si valide structurellement
 */
export const isValidJWTStructure = (token) => {
  if (!token || typeof token !== 'string') return false;
  
  try {
    const parts = token.split('.');
    if (parts.length !== 3) return false;
    
    // Vérifier que chaque partie est en base64 valide
    parts.forEach(part => {
      if (!part) throw new Error('Partie vide');
      atob(part); // Tentative de décodage
    });
    
    return true;
  } catch (error) {
    return false;
  }
};

/**
 * Extraire le payload complet d'un token JWT
 * @param {string} token - Token JWT à décoder
 * @returns {object|null} Payload décodé ou null si erreur
 */
export const getJWTPayload = (token) => {
  if (!token) return null;

  try {
    const parts = token.split('.');
    if (parts.length !== 3) return null;

    return decodeBase64UTF8(parts[1]);
  } catch (error) {
    console.error('Erreur extraction payload JWT:', error);
    return null;
  }
};

/**
 * Vérifier si un token JWT est expiré
 * @param {string} token - Token JWT à vérifier
 * @returns {boolean} True si expiré
 */
export const isTokenExpired = (token) => {
  if (!token) return true;
  
  try {
    const payload = getJWTPayload(token);
    if (!payload || !payload.exp) return true;
    
    const currentTime = Date.now() / 1000;
    return payload.exp < currentTime;
  } catch (error) {
    console.error('Erreur vérification expiration token:', error);
    return true;
  }
};

/**
 * Obtenir les informations utilisateur depuis un token JWT
 * @param {string} token - Token JWT à décoder
 * @returns {object|null} Informations utilisateur ou null si erreur
 */
export const getUserInfoFromToken = (token) => {
  if (!token) return null;
  
  try {
    const payload = getJWTPayload(token);
    if (!payload) return null;
    
    return {
      userId: payload.userId || payload.id || payload.sub || payload.user_id,
      firstName: payload.first_name || payload.firstName,
      lastName: payload.last_name || payload.lastName,  
      email: payload.email,
      role: payload.role,
      exp: payload.exp,
      iat: payload.iat
    };
  } catch (error) {
    console.error('Erreur extraction infos utilisateur:', error);
    return null;
  }
};

export default {
  getUserIdFromToken,
  isValidJWTStructure,
  getJWTPayload,
  isTokenExpired,
  getUserInfoFromToken
};